import React, { memo, useState, useEffect, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import store from 'store';

import AddNewCustomer from 'components/CustomerManagerSlider/AddNewCustomer';
import AutoCompleteCompanyModel from 'components/AutoComplete/Company';
import { Row, Column } from 'components/Layout/Grid';
import AutoComplete from 'components/AutoComplete';
import { formatDocument } from 'utils/Document';
import { Visibility } from 'components/Visibility';
import { Button } from 'components/Button';
import Input from 'components/Input';
import Text from 'components/Text';

import { IBatchRequestResponse } from 'models/Batch';
import { ICustomer } from 'models/Customer';
import { ILegalPersonType } from 'models/Ticket';

import CustomerService from 'services/customer.service';
import { useCustomerDataContext } from 'contexts/OpenProtocol/CustomerData/CustomerDataContext';
import { ObjectIsEmpty } from 'utils/Object';

const CustomerData: React.FC<any> = (props: any) => {

  const {
    company,
    legalPerson,
    disableCustomer,
    customerId = "",
    setCustomerId,
  } = props;

  const dispatch = useDispatch();

  const { addCustomerProtocol, customerProtocol } = useCustomerDataContext();

  const customerMaxResults = 100;

  const [customerName, setCustomerName] = useState(company?.companyName ?? "");
  const [identifier, setIdentifier] = useState(company?.identifier ?? "");
  const [documentNumber, setDocumentNumber] = useState(company?.documentNumber ?? "");

  const [showAutoComplete, setShowAutoComplete] = useState({
    customer: false,
    identifier: false,
    document: false
  });

  useEffect(() => {
    if (ObjectIsEmpty(company))
      return;

    addCustomerProtocol(company);
  }, [company]);

  useEffect(() => {
    if (!disableCustomer)
      clearCustomerData();
  }, [legalPerson]);

  useEffect(() => {
    dispatch({ type: 'SET_OPEN_PROTOCOL_CUSTOMER', data: { id: customerId } });
  }, [customerId, store.getState().customer]);

  const customerService = new CustomerService();

  function setCustomerData(data: any) {
    setShowAutoComplete({ customer: false, identifier: false, document: false });
    setCustomerName(data.companyName);
    setCustomerId(data.id);
    setIdentifier(data.identifier);
    setDocumentNumber(data.documentNumber);

    addCustomerProtocol(data);
  }

  function onChangeCompanyNameInput(e: any) {
    setCustomerName(e.target.value);
    setIdentifier('');
    setCustomerId('');
    setDocumentNumber('');

    resetCustomerProtocol();
  }

  function onBlurCompanyCNPJECPFInput() {
    const cnpjInput = customerProtocol?.documentNumber?.replace(/[.\-\/\s()]/g, '');

    if (cnpjInput !== documentNumber) {
      findByDocument(documentNumber, 1);
    }

    onBlurCompanyCPFInput();
  }

  function onBlurCompanyCPFInput() {
    const cpfInput = customerProtocol?.documentNumber?.replace(/[.\-\/\s()]/g, '');

    if (cpfInput !== documentNumber) {
      findByDocument(documentNumber, 2);
    }
  }

  async function findByDocument(document: string, type: number) {
    if (document !== '') {
      document = document.replace(/[.\-\/\s()]/g, '');
      if (
        (type === 1 && document.length === 14) ||
        (type === 2 && document.length === 11)
      ) {
        await customerService
          .get(undefined, undefined, undefined, {
            documentNumber: { eq: document },
          })
          .then((result: IBatchRequestResponse<ICustomer> | undefined) => {
            if (result) {
              const { data } = result;
              if (data?.length === 1) setCustomerData(data[0]);
              else clearCustomerNameIdIdentifier();
            }
          });
      } else clearCustomerNameIdIdentifier();
    } else clearCustomerData();
  }

  function onBlurCompanyIdentifierInput() {
    if (customerProtocol?.identifier !== identifier) {
      findByIdentifier(identifier);
    }
  }

  async function findByIdentifier(identifierProp: string) {
    if (identifierProp !== '') {
      await customerService
        .get(undefined, undefined, undefined, {
          identifier: { eq: identifierProp },
        })
        .then((result: IBatchRequestResponse<ICustomer> | undefined) => {
          if (result) {
            const { data } = result;
            if (data?.length === 1) setCustomerData(data[0]);
          }
        });

    } else clearCustomerData();
  }

  function clearCustomerNameIdIdentifier() {
    setCustomerName('');
    setCustomerId('');
    setIdentifier('');
  }

  function clearCustomerData() {
    setDocumentNumber('');
    setIdentifier('');
    setCustomerName('');
    setCustomerId('');
  }

  function setNewIdentifier(value: string) {
    clearCustomerData();
    setIdentifier(value);

    resetCustomerProtocol();
  }

  function reqAutoComplete(value: string) {
    return customerService.autoComplete(value.toLowerCase());
  }

  const handleChangeDocumentNumber = (e: ChangeEvent<HTMLInputElement>) => {
    setDocumentNumber(e.target.value);

    resetCustomerProtocol();
  };

  const resetCustomerProtocol = () => {
    addCustomerProtocol({
      identifier: "",
      documentNumber: "",
      useElectronicBillLading: false
    });
  };

  return (
    <Row className="mt-5 flex-md-column">
      <Visibility visible={legalPerson === ILegalPersonType.JURIDICAL_PERSON}>
        <Row className="flex-1">
          <Column className="flex-1 mt-md-2 mr-md-2">
            <Text className="mb-2 flex-md-100 color-blue-gray" size={12}>
              CNPJ/CPF
            </Text>
            <AutoComplete
              show={showAutoComplete.document}
              onSelect={(data: any) => setCustomerData(data)}
              request={(value: string) => reqAutoComplete(value)}
              content={AutoCompleteCompanyModel}
              maxResults={customerMaxResults}
              minLengthSearch={11}
            >
              <Input
                id="cnpj-cpf"
                stateName="documentNumber"
                className="w-100"
                value={formatDocument(documentNumber)}
                onFocus={() => setShowAutoComplete({ customer: false, identifier: false, document: true })}
                type="text"
                required
                name="CNPJ/CPF"
                placeholder="CNPJ/CPF"
                onChange={handleChangeDocumentNumber}
                onBlur={onBlurCompanyCNPJECPFInput}
                disabled={disableCustomer}
                maxLength={18}
              />
            </AutoComplete>
          </Column>
          <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
            <Text className="mb-2 flex-md-100 color-blue-gray" size={12}>
              Código Interno
            </Text>
            <AutoComplete
              show={showAutoComplete.identifier}
              onSelect={(data: any) => setCustomerData(data)}
              request={(value: any) => reqAutoComplete(value)}
              content={AutoCompleteCompanyModel}
              maxResults={customerMaxResults}
              minLengthSearch={6}
            >
              <Input
                id="identifier"
                stateName="identifier"
                className="w-100"
                type="text"
                value={identifier}
                onFocus={() => setShowAutoComplete({ customer: false, identifier: true, document: false })}
                required
                name="Código"
                placeholder="Código"
                onChange={(e: any) => setNewIdentifier(e.target.value)}
                onBlur={onBlurCompanyIdentifierInput}
                disabled={disableCustomer}
              />
            </AutoComplete>
          </Column>
        </Row>
      </Visibility>
      <Column
        className={`${legalPerson !== ILegalPersonType.JURIDICAL_PERSON
          ? ''
          : 'ml-4 ml-sm-0'
          } flex-1 max-w-md-100 mt-md-2`}
      >
        <Text className="mb-2 color-blue-gray" size={12}>
          Cliente
        </Text>
        <AutoComplete
          show={showAutoComplete.customer}
          onSelect={(data: any) => setCustomerData(data)}
          request={(value: any) =>
            customerService.autoComplete(
              value.toLowerCase(),
            )
          }
          content={AutoCompleteCompanyModel}
          maxResults={customerMaxResults}
        >
          <Input
            id="customerName"
            stateName="customerName"
            onChange={(e: any) => onChangeCompanyNameInput(e)}
            onFocus={() => setShowAutoComplete({ customer: true, identifier: false, document: false })}
            required
            name="Cliente"
            className="w-100"
            type="text"
            value={customerName}
            placeholder="Descrição"
            disabled={disableCustomer}
          />
        </AutoComplete>
      </Column>

      <Visibility visible={legalPerson === ILegalPersonType.NATURAL_PERSON}>
        <Column className="ml-4 ml-sm-0 mt-md-2">
          <Text className="mb-2 flex-md-100 color-blue-gray" size={12}>
            CPF/CNPJ
          </Text>
          <AutoComplete
            show={showAutoComplete.document}
            onSelect={(data: any) => setCustomerData(data)}
            request={(value: any) => reqAutoComplete(value)}
            content={AutoCompleteCompanyModel}
            maxResults={customerMaxResults}
            minLengthSearch={11}
          >
            <Input
              id="cpf"
              stateName="documentNumber"
              value={formatDocument(documentNumber)}
              onFocus={() => setShowAutoComplete({ customer: false, identifier: false, document: true })}
              className="w-md-100"
              type="text"
              required
              name="CPF/CNPJ"
              placeholder="CPF/CNPJ"
              onChange={(e: any) => setDocumentNumber(e.target.value)}
              onBlur={onBlurCompanyCNPJECPFInput}
              disabled={disableCustomer}
              maxLength={18}
            />
          </AutoComplete>
        </Column>
        <Row className="align-center mt-2 flex-1 justify-start flex-lg-column ml-4 ml-sm-0">
          <Button
            id="add-customer"
            className="outline mt-2 ml-lg-0 w-lg-100"
            onClick={() =>
              dispatch({
                type: 'OPEN_SLIDER',
                data: {
                  title: 'Adicionar Novo Cliente',
                  subtitle: '',
                  content: AddNewCustomer,
                  state: { setCustomerData },
                },
              })
            }
            width="auto"
          >
            <Text className="color-primary font-bold px-1">
              Adicionar Cliente
            </Text>
          </Button>
        </Row>
      </Visibility>

      <Input stateName="customerId" type="hidden" value={customerId} />

      <Row className="flex-1 ml-4">
        {/*  <Column className='ml-4 ml-md-0 mt-md-4 justify-end justify-md-none'>
                  <Button onClick={() => {}} className='bg-primary outline' width='auto' style={{ height: 42 }}>
                    <Text className='color-primary font-bold px-4'>Adicionar Cliente</Text>
                  </Button>
             </Column>
        */}
      </Row>
    </Row>
  );
};

export default memo(CustomerData);
